/**
 * Reward Breakdown — per-transaction explainer popover.
 *
 * Shows two columns when applicable:
 *   1. EARNED — every `portion` returned by the rules engine for the card
 *      the user actually charged (primary, spillover, milestone, tier).
 *   2. MISSED — when the engine identified a better alternative, we render
 *      the best card's portions side-by-side so users can see exactly WHY
 *      they missed value (e.g. "Axis Magnus would have earned ₹15,000 via
 *      Travel EDGE 5X vs your ₹1,000 base earn").
 *
 * The popover is intentionally text-dense (mono-num + tiny inline labels)
 * so it stays within Shadcn's standard popover footprint on mobile.
 */
import { Info, TrendingUp, AlertTriangle, Sparkles, Trophy, Layers } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { formatINR, formatNumber } from '@/lib/format';

const KIND_META = {
  primary:   { icon: TrendingUp, label: 'Base earn',      color: 'text-emerald-500' },
  spillover: { icon: Layers,     label: 'Over-cap',       color: 'text-cyan-500' },
  milestone: { icon: Trophy,     label: 'Milestone bonus', color: 'text-amber-500' },
  tier:      { icon: Sparkles,   label: 'Tier bonus',     color: 'text-violet-500' },
};

const PortionList = ({ portions, currency }) => {
  if (!portions?.length) {
    return (
      <div className="text-[11px] text-muted-foreground italic">
        No matching reward rule fired for this transaction.
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {portions.map((p, idx) => {
        const meta = KIND_META[p.kind] || KIND_META.primary;
        const Icon = meta.icon;
        return (
          <li
            key={`${p.kind}-${p.rule_id || idx}`}
            data-testid={`reward-portion-${p.kind}-${idx}`}
            className="flex items-start gap-2"
          >
            <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${meta.color}`} aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium truncate" title={p.rule_label || meta.label}>
                  {p.rule_label || meta.label}
                </span>
                <span className="text-[11px] font-mono-num text-emerald-600 dark:text-emerald-400 shrink-0">
                  +{formatINR(p.reward_value_inr || 0)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-2 text-[10px] text-muted-foreground">
                <span className="truncate">
                  {p.kind === 'primary' || p.kind === 'spillover'
                    ? `${formatINR(p.eligible_amount_inr || 0)} eligible`
                    : meta.label}
                </span>
                <span className="font-mono-num shrink-0">
                  {formatNumber(p.reward_raw || 0)} {(p.currency || currency || 'pts').trim() || 'pts'}
                </span>
              </div>
              {p.note && (
                <div className="text-[10px] text-muted-foreground mt-0.5 italic line-clamp-2">
                  {p.note}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const RewardBreakdownPopover = ({ tx, trigger }) => {
  const earned = tx.portions || tx.reward_portions || [];
  const missedPortions = tx.best_card_portions || tx.bestCardPortions || [];
  const isBest = !!tx.best;
  const missedAmount = Number(tx.missed ?? tx.missed_inr ?? 0);
  const earnedValueInr = Number(tx.reward_value_inr ?? 0);
  const hasAnythingToShow = earned.length > 0 || missedPortions.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full text-muted-foreground hover:text-foreground"
            aria-label="Reward breakdown"
            data-testid={`reward-breakdown-trigger-${tx.id}`}
          >
            <Info className="h-3.5 w-3.5" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-4 space-y-4"
        data-testid={`reward-breakdown-popover-${tx.id}`}
      >
        {!hasAnythingToShow ? (
          <div className="text-[12px] text-muted-foreground">
            We didn&rsquo;t store a breakdown for this older transaction.
            Re-saving it will refresh the math.
          </div>
        ) : (
          <>
            <section>
              <header className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Earned on {tx.card_name || tx.cardName || 'this card'}
                </span>
                <span
                  className="text-xs font-mono-num font-medium text-emerald-600 dark:text-emerald-400"
                  data-testid={`reward-breakdown-earned-total-${tx.id}`}
                >
                  +{formatINR(earnedValueInr)}
                </span>
              </header>
              <PortionList portions={earned} currency={tx.reward_currency} />
            </section>

            {!isBest && missedAmount > 0 && (
              <section className="pt-3 border-t border-border/50">
                <header className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Missed via {tx.better_card_name || tx.betterCardName || 'a better card'}
                  </span>
                  <span
                    className="text-xs font-mono-num font-medium text-amber-600 dark:text-amber-400"
                    data-testid={`reward-breakdown-missed-total-${tx.id}`}
                  >
                    {formatINR(missedAmount)}
                  </span>
                </header>
                <PortionList portions={missedPortions} currency={tx.reward_currency} />
                <p className="mt-2 text-[10px] text-muted-foreground italic">
                  Missed = best alternative reward − reward earned on this card,
                  computed only for this transaction (not an annual estimate).
                </p>
              </section>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
